import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridRowId,
    GridRowsProp,
    GridToolbar,
} from "@mui/x-data-grid";
import styled from "styled-components";
import { Company } from "../../types";
import { EmployeeContext } from "../../context/EmployeeContext";
import { useContext, useEffect, useState } from "react";
import { SoecoButton } from "../../components/SoecoButton";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { getRolePriority } from "../../utils/util";
import { InputAdornment, Pagination, PaginationItem, Stack, TextField } from "@mui/material";
import { ArrowLeft, ArrowRight, Search, SearchIcon } from "lucide-react";

type Props = {
    company: Company;
    companies: Company[];
    companyId: string;
};

export const ManageCompaniesList = (props: Props) => {
    const navigate = useNavigate();
    const { employee } = useContext(EmployeeContext);

    const [page, setPage] = useState(1);
    const [pageSize] = useState(100);

    const handleCompanyEdit = (id: GridRowId) => {
        navigate(`/manageCompanies/${id}/${props.companyId}`);
    };

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Name",
            align: "left",
            flex: 3,
            editable: false,
        },

        {
            field: "edit",
            align: "left",
            editable: false,
            renderHeader: () => null,
            renderCell: (params: GridRenderCellParams<unknown, RowModel, RowModel>) => {
                return (
                    <>
                        {getRolePriority(employee.role) === 2 ||
                        getRolePriority(employee.role) === 3 ||
                        getRolePriority(employee.role) === 4 ? (
                            <TableActions>
                                <SoecoButton
                                    icon={true}
                                    size="small"
                                    onClick={() => handleCompanyEdit(params.id)}
                                    disabled={true}
                                >
                                    <EditIcon fontSize="inherit" />
                                </SoecoButton>
                            </TableActions>
                        ) : (
                            <TableActions>
                                <SoecoButton
                                    icon={true}
                                    size="small"
                                    onClick={() => handleCompanyEdit(params.id)}
                                >
                                    <EditIcon fontSize="inherit" />
                                </SoecoButton>
                            </TableActions>
                        )}
                    </>
                );
            },
        },
    ];

    type RowModel = {
        id: string;
        name: string | undefined;
    };

    const rows: GridRowsProp<RowModel> = [
        ...props.companies.map(company => ({
            id: company.id,
            name: company.name,
        })),
    ];

    const numberOfPages = Math.ceil(rows.length / pageSize);
    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
    };

    return (
        <CompanyListWrapper>
            <DataGrid
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                components={{ Toolbar: GridToolbar }}
                rows={rows}
                columns={columns}
                pageSize={pageSize}
                page={page - 1}
                rowsPerPageOptions={[100]}
                hideFooter
                componentsProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: {
                            debounceMs: 500,
                            placeholder: "Search",
                            variant: "outlined",
                        },
                        sx: {
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexWrap: "nowrap",
                            "& .MuiButton-root": {
                                backgroundColor: "white",
                                color: "#8c8c8c",
                                border: "1px solid #9e9e9e",
                                marginLeft: "20px",
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: "600",
                                height: "42px",
                                minHeight: "42px",
                                padding: "0 8px",
                                fontSize: "14px",
                            },
                            "& .MuiTextField-root": {
                                color: "red",
                                borderRadius: "20px",
                                placeholder: "OK",

                                "& .MuiInputBase-root": {
                                    borderRadius: "50px",
                                    color: "black",
                                    border: "1px solid #9e9e9e",
                                    marginRight: "20px",
                                    fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                    fontWeight: "400",
                                    height: "42px",
                                    minHeight: "42px",
                                    padding: "0 8px",
                                    fontSize: "14px",
                                },
                            },
                        },
                    },
                    pagination: {
                        count: numberOfPages,
                    },
                }}
            />

            {/* Customized pagination footer */}
            <Stack
                spacing={2}
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                marginTop={2}
            >
                <Pagination
                    shape="rounded"
                    color="primary"
                    page={page}
                    onChange={handlePageChange}
                    count={numberOfPages}
                    renderItem={item => (
                        <PaginationItem
                            {...item}
                            components={{
                                previous: () => (
                                    <ArrowLeft style={{ cursor: "pointer" }} size={16} />
                                ),
                                next: () => <ArrowRight style={{ cursor: "pointer" }} size={16} />,
                            }}
                            sx={{
                                backgroundColor: item.selected ? "black" : "transparent",
                                color: item.selected ? "white" : "black",
                                "&:hover": {
                                    backgroundColor: item.selected ? "black" : "lightgrey",
                                },
                                fontFamily: "'Yllw Sans a1 241219', sans-serif",
                            }}
                        />
                    )}
                />
            </Stack>
        </CompanyListWrapper>
    );
};

const CompanyListWrapper = styled.div`
    width: 100%;
    height: 100%;
    max-height: 100%;
`;

const TableActions = styled.div`
    display: flex;
    font-size: 6px;
`;
